import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Users, Ticket, Shield, Mail, Plus, Minus, Archive, ArchiveRestore, ChevronDown, ChevronRight, Trash2, LayoutGrid, Table, Euro, Calendar, UserCheck, UserX, Copy, Check, Send, X, UserPlus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Progress } from '@/components/ui/progress';
import { StrippenkaartVisual } from '@/components/ui/strippenkaart-visual';
import { EventRegistration } from '@/lib/supabase';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';

type UserWithStrippenkaarten = {
  id: string;
  email: string;
  created_at: string;
  strippenkaarten: {
    id: string;
    resterende_strippen: number;
    totaal_strippen: number;
    gekocht_op: string;
  }[];
};

export default function Admin() {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState<UserWithStrippenkaarten[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [expandedArchives, setExpandedArchives] = useState<Set<string>>(new Set());
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards');
  
  // Event registraties state
  const [selectedEventId, setSelectedEventId] = useState<string>('');
  const [eventRegistrations, setEventRegistrations] = useState<EventRegistration[]>([]);
  const [loadingRegistrations, setLoadingRegistrations] = useState(false);
  const [activeTab, setActiveTab] = useState<'users' | 'registrations' | 'invitations'>('users');
  const [copiedEmail, setCopiedEmail] = useState<string | null>(null);
  
  // Email verzend state
  const [selectedRegistrations, setSelectedRegistrations] = useState<Set<string>>(new Set());
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);

  // Opstellingenmiddagen (sync met Agenda.tsx)
  const opstellingenmiddagen = [
    {
      id: 'opstellingenmiddag-2026-01-29',
      naam: 'Opstellingenmiddag',
      datum: "29 Januari 2026",
      tijd: "13:00 - 16:30",
      locatie: "Het Atelier, Uitgeest",
      type: 'opstellingenmiddag' as const,
    },
    {
      id: 'opstellingenmiddag-2026-03-05',
      naam: 'Opstellingenmiddag',
      datum: "5 Maart 2026",
      tijd: "13:00 - 16:30",
      locatie: "Het Atelier, Uitgeest",
      type: 'opstellingenmiddag' as const,
    },
    {
      id: 'opstellingenmiddag-2026-04-16',
      naam: 'Opstellingenmiddag',
      datum: "16 April 2026",
      tijd: "13:00 - 16:30",
      locatie: "Het Atelier, Uitgeest",
      type: 'opstellingenmiddag' as const,
    }
  ];

  // Alle events (opstellingenmiddagen + themadag)
  const allEvents = [
    {
      id: 'themadag-burnout-2026-10-22',
      naam: 'Themadag Burn-out',
      datum: "22 oktober 2026",
      tijd: "13:00 - 17:00",
      locatie: "Stadsoase naar Zee, Amsterdam",
      type: 'themadag' as const,
    },
    ...opstellingenmiddagen,
  ];

  const selectedEvent = allEvents.find(e => e.id === selectedEventId);
  const isThemadag = selectedEvent?.type === 'themadag';

  useEffect(() => {
    if (!isAdmin) {
      toast.error('Geen toegang - alleen voor admins');
      navigate('/');
      return;
    }
    loadUsers();
  }, [isAdmin, navigate]);

  // Laad registraties wanneer event geselecteerd wordt
  useEffect(() => {
    if (selectedEventId) {
      loadEventRegistrations(selectedEventId);
    } else {
      setEventRegistrations([]);
    }
  }, [selectedEventId]);

  const loadEventRegistrations = async (eventId: string) => {
    setLoadingRegistrations(true);
    try {
      const { data, error } = await supabase
        .from('event_registrations')
        .select('*')
        .eq('event_id', eventId)
        .eq('status', 'bevestigd')
        .order('aangemeld_op', { ascending: false });

      if (error) {
        console.error('Error loading event registrations:', error);
        toast.error('Fout bij laden van inschrijvingen');
        setEventRegistrations([]);
      } else {
        setEventRegistrations(data || []);
      }
    } catch (err) {
      console.error('Exception loading registrations:', err);
      toast.error('Onverwachte fout bij laden van inschrijvingen');
      setEventRegistrations([]);
    } finally {
      setLoadingRegistrations(false);
    }
  };

  const copyEmailToClipboard = async (email: string) => {
    if (!email || email === 'Email onbekend') {
      toast.error('Geen emailadres beschikbaar');
      return;
    }

    try {
      await navigator.clipboard.writeText(email);
      setCopiedEmail(email);
      toast.success('Emailadres gekopieerd naar klembord');
      // Reset na 2 seconden
      setTimeout(() => setCopiedEmail(null), 2000);
    } catch (err) {
      console.error('Failed to copy email:', err);
      toast.error('Fout bij kopiëren van emailadres');
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (activeTab === 'invitations') {
      if (checked) {
        const allIds = new Set(users.map(u => u.id));
        setSelectedUsers(allIds);
      } else {
        setSelectedUsers(new Set());
      }
    } else {
      if (checked) {
        const allIds = new Set(eventRegistrations.map(r => r.id));
        setSelectedRegistrations(allIds);
      } else {
        setSelectedRegistrations(new Set());
      }
    }
  };

  const handleSelectRegistration = (registrationId: string, checked: boolean) => {
    const newSelected = new Set(selectedRegistrations);
    if (checked) {
      newSelected.add(registrationId);
    } else {
      newSelected.delete(registrationId);
    }
    setSelectedRegistrations(newSelected);
  };

  const handleSelectUser = (userId: string, checked: boolean) => {
    const newSelected = new Set(selectedUsers);
    if (checked) {
      newSelected.add(userId);
    } else {
      newSelected.delete(userId);
    }
    setSelectedUsers(newSelected);
  };

  const getSelectedEmails = (): string[] => {
    if (activeTab === 'invitations') {
      // Voor uitnodigingen: gebruik geselecteerde gebruikers
      return users
        .filter(u => selectedUsers.has(u.id))
        .map(u => u.email)
        .filter((email): email is string => email !== null && email !== 'Email onbekend' && email.trim() !== '');
    } else {
      // Voor registraties: gebruik geselecteerde registraties
      return eventRegistrations
        .filter(r => selectedRegistrations.has(r.id))
        .map(r => r.email)
        .filter((email): email is string => email !== null && email !== 'Email onbekend' && email.trim() !== '');
    }
  };

  const sendEmails = async () => {
    const emails = getSelectedEmails();
    
    if (emails.length === 0) {
      toast.error('Geen geldige emailadressen geselecteerd');
      return;
    }

    if (!emailSubject.trim()) {
      toast.error('Onderwerp is verplicht');
      return;
    }

    if (!emailMessage.trim()) {
      toast.error('Bericht is verplicht');
      return;
    }

    // Check if we're in development/localhost
    const isDevelopment = import.meta.env.DEV;
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    
    if (isDevelopment || isLocalhost) {
      toast.error('Email verzending werkt alleen in productie of met Netlify Dev. Gebruik "netlify dev" om lokaal te testen.');
      return;
    }

    setSendingEmail(true);

    try {
      const response = await fetch('/.netlify/functions/admin-send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          emails,
          subject: emailSubject,
          message: emailMessage
        })
      });

      // Check if response is ok before parsing JSON
      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = 'Fout bij verzenden van emails';
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.error || errorMessage;
        } catch {
          errorMessage = `Server error: ${response.status} ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();

      if (data.error) {
        throw new Error(data.error);
      }

      toast.success(`${data.sent} ${data.sent === 1 ? 'email' : 'emails'} succesvol verzonden${data.failed > 0 ? `, ${data.failed} mislukt` : ''}`);
      
      // Reset form
      setEmailSubject('');
      setEmailMessage('');
      setSelectedRegistrations(new Set());
      setSelectedUsers(new Set());
      setEmailModalOpen(false);
    } catch (error) {
      console.error('Error sending emails:', error);
      const errorMessage = error instanceof Error ? error.message : 'Fout bij verzenden van emails';
      toast.error(errorMessage);
    } finally {
      setSendingEmail(false);
    }
  };

  const loadUsers = async () => {
    setLoading(true);

    // Get all profiles (alle geregistreerde users)
    const { data: profiles, error: profilesError } = await supabase
      .from('profiles')
      .select('*')
      .order('created_at', { ascending: false });

    if (profilesError) {
      console.error('Error loading profiles:', profilesError);
      toast.error('Fout bij laden van gebruikers');
      setLoading(false);
      return;
    }

    // Get all strippenkaarten with all columns including archived
    const { data: strippenkaarten, error: strippenError } = await supabase
      .from('strippenkaarten')
      .select('*')
      .order('gekocht_op', { ascending: false });

    if (strippenError) {
      console.error('Error loading strippenkaarten:', strippenError);
      toast.error('Fout bij laden van strippenkaarten');
      setLoading(false);
      return;
    }

    // Create user map met ALLE profiles
    const userMap = new Map<string, UserWithStrippenkaarten>();
    
    // Eerst alle profiles toevoegen (ook zonder strippenkaarten)
    profiles?.forEach((profile) => {
      userMap.set(profile.id, {
        id: profile.id,
        email: profile.email,
        created_at: profile.created_at,
        strippenkaarten: [],
      });
    });

    // Dan strippenkaarten toevoegen aan de juiste users
    strippenkaarten?.forEach((kaart) => {
      if (userMap.has(kaart.user_id)) {
        userMap.get(kaart.user_id)?.strippenkaarten.push({
          id: kaart.id,
          user_id: kaart.user_id,
          resterende_strippen: kaart.resterende_strippen,
          totaal_strippen: kaart.totaal_strippen,
          gekocht_op: kaart.gekocht_op,
          archived: kaart.archived === true, // Convert null to false
        });
      }
    });

    setUsers(Array.from(userMap.values()));
    setLoading(false);
  };

  const addStrippenkaart = async (userId: string) => {
    setActionLoading(userId);
    
    try {
      // Use RPC function instead of direct insert to bypass RLS issues
      const { data, error } = await supabase
        .rpc('admin_add_strippenkaart', {
          target_user_id: userId,
          initial_strippen: 3
        });

      if (error) {
        console.error('Error adding strippenkaart:', error);
        toast.error('Fout bij toevoegen van strippenkaart: ' + error.message);
        setActionLoading(null);
        return;
      }
      
      if (data) {
        toast.success('Nieuwe strippenkaart toegevoegd');
        // Small delay to ensure database replication
        await new Promise(resolve => setTimeout(resolve, 500));
        await loadUsers();
        setActionLoading(null);
      } else {
        setActionLoading(null);
      }
    } catch (err) {
      console.error('Exception adding strippenkaart:', err);
      toast.error('Onverwachte fout bij toevoegen van strippenkaart');
      setActionLoading(null);
    }
  };

  const adjustStrippen = async (strippenkaartId: string, adjustment: number) => {
    setActionLoading(strippenkaartId);
    
    // Find current strippenkaart
    const currentKaart = users
      .flatMap(u => u.strippenkaarten)
      .find(k => k.id === strippenkaartId);
    
    if (!currentKaart) {
      toast.error('Strippenkaart niet gevonden');
      setActionLoading(null);
      return;
    }

    const newValue = currentKaart.resterende_strippen + adjustment;
    
    if (newValue < 0) {
      toast.error('Kan niet lager dan 0 strippen');
      setActionLoading(null);
      return;
    }

    if (newValue > currentKaart.totaal_strippen) {
      toast.error('Kan niet meer dan totaal strippen');
      setActionLoading(null);
      return;
    }

    const { error } = await supabase
      .from('strippenkaarten')
      .update({ resterende_strippen: newValue })
      .eq('id', strippenkaartId);

    if (error) {
      console.error('Error adjusting strippen:', error);
      toast.error('Fout bij aanpassen van strippen');
    } else {
      toast.success(`${adjustment > 0 ? 'Strip toegevoegd' : 'Strip verwijderd'}`);
      await loadUsers();
    }
    setActionLoading(null);
  };

  const toggleArchive = async (strippenkaartId: string, currentArchived: boolean) => {
    setActionLoading(strippenkaartId);
    
    const { error } = await supabase
      .from('strippenkaarten')
      .update({ archived: !currentArchived })
      .eq('id', strippenkaartId);

    if (error) {
      console.error('Error toggling archive:', error);
      toast.error('Fout bij archiveren');
    } else {
      toast.success(currentArchived ? 'Strippenkaart teruggezet' : 'Strippenkaart gearchiveerd');
      await loadUsers();
    }
    setActionLoading(null);
  };

  const deleteStrippenkaart = async (strippenkaartId: string) => {
    if (!confirm('Weet je zeker dat je deze strippenkaart permanent wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.')) {
      return;
    }

    setActionLoading(strippenkaartId);
    
    const { error } = await supabase
      .from('strippenkaarten')
      .delete()
      .eq('id', strippenkaartId);

    if (error) {
      console.error('Error deleting strippenkaart:', error);
      toast.error('Fout bij verwijderen: ' + error.message);
    } else {
      toast.success('Strippenkaart verwijderd');
      await loadUsers();
    }
    setActionLoading(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const totalUsers = users.length;
  const totalStrippenkaarten = users.reduce((sum, u) => sum + u.strippenkaarten.length, 0);
  const totalStripsUsed = users.reduce((sum, u) => 
    sum + u.strippenkaarten.reduce((s, k) => s + (k.totaal_strippen - k.resterende_strippen), 0), 0
  );
  const totalRevenue = totalStrippenkaarten * 100; // €100 per strippenkaart

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#efe8dc' }}>
      <div className="container mx-auto px-4 py-6 md:py-12">
        {/* Header */}
        <div className="mb-6 md:mb-8">
          <div className="flex flex-col gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Shield className="h-6 w-6 md:h-8 md:w-8 text-primary" />
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold" style={{ color: '#6b4912' }}>Admin <span className="italic font-light font-serif">Dashboard</span></h1>
              </div>
              <p className="text-sm md:text-base" style={{ color: '#6b4912' }}>Beheer gebruikers en strippenkaarten</p>
            </div>
            {/* Tabs */}
            <div className="flex gap-2 border-b">
              <Button
                variant={activeTab === 'users' ? 'default' : 'ghost'}
                onClick={() => setActiveTab('users')}
                className="rounded-b-none"
              >
                <Users className="h-4 w-4 mr-2" />
                Gebruikers & Strippenkaarten
              </Button>
              <Button
                variant={activeTab === 'registrations' ? 'default' : 'ghost'}
                onClick={() => setActiveTab('registrations')}
                className="rounded-b-none"
              >
                <Calendar className="h-4 w-4 mr-2" />
                Inschrijvingen
              </Button>
              <Button
                variant={activeTab === 'invitations' ? 'default' : 'ghost'}
                onClick={() => setActiveTab('invitations')}
                className="rounded-b-none"
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Uitnodigen
              </Button>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="flex border rounded-lg p-1 bg-white w-full sm:w-auto">
                <Button
                  variant={viewMode === 'cards' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('cards')}
                  className="gap-2 flex-1 sm:flex-initial"
                >
                  <LayoutGrid className="h-4 w-4" />
                  <span className="hidden xs:inline">Cards</span>
                </Button>
                <Button
                  variant={viewMode === 'table' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('table')}
                  className="gap-2 flex-1 sm:flex-initial"
                >
                  <Table className="h-4 w-4" />
                  <span className="hidden xs:inline">Tabel</span>
                </Button>
              </div>
              <Button onClick={() => navigate('/')} variant="outline" className="w-full sm:w-auto">
                Terug naar site
              </Button>
            </div>
          </div>
        </div>

        {/* Stats Overview - alleen tonen bij users tab */}
        {activeTab === 'users' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6 mb-6 md:mb-8">
          <Card>
            <CardHeader className="pb-2 md:pb-3">
              <CardTitle className="text-xs md:text-sm font-medium" style={{ color: '#6b4912' }}>Totaal Gebruikers</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl md:text-3xl font-bold">{totalUsers}</div>
              <p className="text-xs mt-1" style={{ color: '#6b4912' }}>
                <Users className="inline h-3 w-3 mr-1" />
                Geregistreerd
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2 md:pb-3">
              <CardTitle className="text-xs md:text-sm font-medium" style={{ color: '#6b4912' }}>Strippenkaarten</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl md:text-3xl font-bold">{totalStrippenkaarten}</div>
              <p className="text-xs mt-1" style={{ color: '#6b4912' }}>
                <Ticket className="inline h-3 w-3 mr-1" />
                Uitgegeven
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2 md:pb-3">
              <CardTitle className="text-xs md:text-sm font-medium" style={{ color: '#6b4912' }}>Gebruikte Strippen</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl md:text-3xl font-bold">{totalStripsUsed}</div>
              <p className="text-xs mt-1" style={{ color: '#6b4912' }}>
                Totaal verbruik
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2 md:pb-3">
              <CardTitle className="text-xs md:text-sm font-medium" style={{ color: '#6b4912' }}>Omzet</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl md:text-3xl font-bold">€{totalRevenue.toLocaleString('nl-NL')}</div>
              <p className="text-xs mt-1" style={{ color: '#6b4912' }}>
                {totalStrippenkaarten} kaarten à €100
              </p>
            </CardContent>
          </Card>
        </div>
        )}

        {/* Event Registraties Sectie */}
        {activeTab === 'registrations' && (
          <div className="space-y-6">
            {/* Event Selector */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5" style={{ color: '#6b4912' }} />
                  Selecteer <span className="italic font-light font-serif">Evenement</span>
                </CardTitle>
                <CardDescription>
                  Kies een evenement om de inschrijvingen te bekijken
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Select value={selectedEventId} onValueChange={setSelectedEventId}>
                  <SelectTrigger className="w-full md:w-[400px]">
                    <SelectValue placeholder="Selecteer een evenement..." />
                  </SelectTrigger>
                  <SelectContent>
                    {allEvents.map((event) => (
                      <SelectItem key={event.id} value={event.id}>
                        {event.naam} - {event.datum} - {event.locatie}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>

            {/* Registraties Overzicht */}
            {selectedEventId && (
              <div>
                {loadingRegistrations ? (
                  <Card>
                    <CardContent className="py-12 text-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
                      <p className="mt-4 text-sm" style={{ color: '#6b4912' }}>Laden...</p>
                    </CardContent>
                  </Card>
                ) : eventRegistrations.length === 0 ? (
                  <Card>
                    <CardContent className="py-12 text-center">
                      <Calendar className="h-12 w-12 mx-auto mb-4" style={{ color: '#6b4912' }} />
                      <p className="text-sm" style={{ color: '#6b4912' }}>Nog geen inschrijvingen voor dit evenement</p>
                    </CardContent>
                  </Card>
                ) : (
                  <>
                    {/* Selectie Bar */}
                    {eventRegistrations.length > 0 && (
                      <Card className="mb-4">
                        <CardContent className="pt-6">
                          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                            <div className="flex items-center gap-3">
                              <input
                                type="checkbox"
                                id="select-all"
                                checked={selectedRegistrations.size === eventRegistrations.length && eventRegistrations.length > 0}
                                onChange={(e) => handleSelectAll(e.target.checked)}
                                className="w-4 h-4 rounded border-gray-300 text-[#854C73] focus:ring-[#854C73]"
                              />
                              <Label htmlFor="select-all" className="text-sm font-medium cursor-pointer">
                                Selecteer alle ({eventRegistrations.length})
                              </Label>
                            </div>
                            {selectedRegistrations.size > 0 && (
                              <div className="flex items-center gap-3">
                                <span className="text-sm text-gray-600">
                                  {selectedRegistrations.size} {selectedRegistrations.size === 1 ? 'persoon' : 'personen'} geselecteerd
                                </span>
                                <Button 
                                  onClick={() => setEmailModalOpen(true)}
                                  className="bg-[#854C73] hover:bg-[#590D0D]"
                                >
                                  <Send className="h-4 w-4 mr-2" />
                                  Verstuur email
                                </Button>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    )}
                    
                    {isThemadag ? (
                      // Themadag layout: één lijst met alle deelnemers
                      <Card>
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2">
                            <UserCheck className="h-5 w-5" style={{ color: '#854C73' }} />
                            Deelnemers
                            <Badge variant="secondary" className="ml-auto">
                              {eventRegistrations.length}
                            </Badge>
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-3">
                            {eventRegistrations.map((registration) => {
                              const naam = registration.voornaam && registration.achternaam 
                                ? `${registration.voornaam} ${registration.achternaam}`.trim()
                                : registration.email || 'Naam onbekend';
                              
                              return (
                                <div
                                  key={registration.id}
                                  className={`p-4 border rounded-lg bg-[#FAEBEE]/30 border-[#854C73]/20 ${selectedRegistrations.has(registration.id) ? 'ring-2 ring-[#854C73]' : ''}`}
                                >
                                  <div className="flex items-start gap-3">
                                    <input
                                      type="checkbox"
                                      checked={selectedRegistrations.has(registration.id)}
                                      onChange={(e) => handleSelectRegistration(registration.id, e.target.checked)}
                                      className="mt-1 w-4 h-4 rounded border-gray-300 text-[#854C73] focus:ring-[#854C73] flex-shrink-0"
                                    />
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2 mb-1">
                                        <Mail className="h-4 w-4 flex-shrink-0" style={{ color: '#854C73' }} />
                                        <span className="font-medium text-sm break-all">{naam}</span>
                                        {registration.email && registration.email !== 'Email onbekend' && (
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-6 w-6 p-0 hover:bg-[#854C73]/10"
                                            onClick={() => copyEmailToClipboard(registration.email!)}
                                            title="Kopieer emailadres"
                                          >
                                            {copiedEmail === registration.email ? (
                                              <Check className="h-3.5 w-3.5" style={{ color: '#854C73' }} />
                                            ) : (
                                              <Copy className="h-3.5 w-3.5" style={{ color: '#854C73' }} />
                                            )}
                                          </Button>
                                        )}
                                      </div>
                                      <div className="flex flex-wrap gap-2 mt-2">
                                        {registration.email && (
                                          <span className="text-xs text-gray-600">{registration.email}</span>
                                        )}
                                        {registration.telefoon && (
                                          <span className="text-xs text-gray-600">Tel: {registration.telefoon}</span>
                                        )}
                                        {registration.organisatie && (
                                          <span className="text-xs text-gray-600">Org: {registration.organisatie}</span>
                                        )}
                                        {registration.betaald_bedrag && (
                                          <Badge variant="outline" className="text-xs bg-green-50">
                                            €{registration.betaald_bedrag.toFixed(2)}
                                          </Badge>
                                        )}
                                      </div>
                                      {registration.bericht && (
                                        <p className="text-xs text-gray-600 mt-2 italic">{registration.bericht}</p>
                                      )}
                                      <p className="text-xs text-gray-500 mt-2">
                                        Ingeschreven: {new Date(registration.aangemeld_op).toLocaleString('nl-NL')}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                            {eventRegistrations.length === 0 && (
                              <p className="text-sm text-gray-500 text-center py-4">Geen deelnemers ingeschreven</p>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ) : (
                      // Opstellingenmiddag layout: vraagstellers/representanten kolommen
                      <div className="grid md:grid-cols-2 gap-6">
                        {/* Vraagstellers Kolom */}
                      <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <UserCheck className="h-5 w-5" style={{ color: '#854C73' }} />
                          Vraagstellers
                          <Badge variant="secondary" className="ml-auto">
                            {eventRegistrations.filter(r => r.rol === 'vraagsteller').length}
                          </Badge>
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          {eventRegistrations
                            .filter(r => r.rol === 'vraagsteller')
                            .map((registration) => (
                              <div
                                key={registration.id}
                                className={`p-4 border rounded-lg bg-[#FAEBEE]/30 border-[#854C73]/20 ${selectedRegistrations.has(registration.id) ? 'ring-2 ring-[#854C73]' : ''}`}
                              >
                                <div className="flex items-start gap-3">
                                  <input
                                    type="checkbox"
                                    checked={selectedRegistrations.has(registration.id)}
                                    onChange={(e) => handleSelectRegistration(registration.id, e.target.checked)}
                                    className="mt-1 w-4 h-4 rounded border-gray-300 text-[#854C73] focus:ring-[#854C73] flex-shrink-0"
                                  />
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                      <Mail className="h-4 w-4 flex-shrink-0" style={{ color: '#854C73' }} />
                                      <span className="font-medium text-sm break-all">
                                        {registration.user_id 
                                          ? (registration.email || 'Email onbekend')
                                          : (registration.email || 'Email onbekend')
                                        }
                                      </span>
                                      {registration.email && registration.email !== 'Email onbekend' && (
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-6 w-6 p-0 hover:bg-[#854C73]/10"
                                          onClick={() => copyEmailToClipboard(registration.email!)}
                                          title="Kopieer emailadres"
                                        >
                                          {copiedEmail === registration.email ? (
                                            <Check className="h-3.5 w-3.5" style={{ color: '#854C73' }} />
                                          ) : (
                                            <Copy className="h-3.5 w-3.5" style={{ color: '#854C73' }} />
                                          )}
                                        </Button>
                                      )}
                                    </div>
                                    {registration.user_id ? (
                                      <Badge variant="outline" className="text-xs mt-1">
                                        Geregistreerde gebruiker
                                      </Badge>
                                    ) : (
                                      <Badge variant="outline" className="text-xs mt-1 bg-yellow-50">
                                        Alleen email
                                      </Badge>
                                    )}
                                    <p className="text-xs text-gray-500 mt-2">
                                      Ingeschreven: {new Date(registration.aangemeld_op).toLocaleString('nl-NL')}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            ))}
                          {eventRegistrations.filter(r => r.rol === 'vraagsteller').length === 0 && (
                            <p className="text-sm text-gray-500 text-center py-4">Geen vraagstellers ingeschreven</p>
                          )}
                        </div>
                      </CardContent>
                    </Card>

                    {/* Representanten Kolom */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <UserX className="h-5 w-5" style={{ color: '#6b4912' }} />
                          Representanten
                          <Badge variant="secondary" className="ml-auto">
                            {eventRegistrations.filter(r => r.rol === 'representant').length}
                          </Badge>
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          {eventRegistrations
                            .filter(r => r.rol === 'representant')
                            .map((registration) => (
                              <div
                                key={registration.id}
                                className={`p-4 border rounded-lg bg-[#FAEBEE]/30 border-[#6b4912]/20 ${selectedRegistrations.has(registration.id) ? 'ring-2 ring-[#6b4912]' : ''}`}
                              >
                                <div className="flex items-start gap-3">
                                  <input
                                    type="checkbox"
                                    checked={selectedRegistrations.has(registration.id)}
                                    onChange={(e) => handleSelectRegistration(registration.id, e.target.checked)}
                                    className="mt-1 w-4 h-4 rounded border-gray-300 text-[#6b4912] focus:ring-[#6b4912] flex-shrink-0"
                                  />
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                      <Mail className="h-4 w-4 flex-shrink-0" style={{ color: '#6b4912' }} />
                                      <span className="font-medium text-sm break-all">
                                        {registration.user_id 
                                          ? (registration.email || 'Email onbekend')
                                          : (registration.email || 'Email onbekend')
                                        }
                                      </span>
                                      {registration.email && registration.email !== 'Email onbekend' && (
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-6 w-6 p-0 hover:bg-[#6b4912]/10"
                                          onClick={() => copyEmailToClipboard(registration.email!)}
                                          title="Kopieer emailadres"
                                        >
                                          {copiedEmail === registration.email ? (
                                            <Check className="h-3.5 w-3.5" style={{ color: '#6b4912' }} />
                                          ) : (
                                            <Copy className="h-3.5 w-3.5" style={{ color: '#6b4912' }} />
                                          )}
                                        </Button>
                                      )}
                                    </div>
                                    {registration.user_id ? (
                                      <Badge variant="outline" className="text-xs mt-1">
                                        Geregistreerde gebruiker
                                      </Badge>
                                    ) : (
                                      <Badge variant="outline" className="text-xs mt-1 bg-yellow-50">
                                        Alleen email
                                      </Badge>
                                    )}
                                    {registration.strippenkaart_id && (
                                      <Badge variant="outline" className="text-xs mt-1 ml-1 bg-green-50">
                                        Via strippenkaart
                                      </Badge>
                                    )}
                                    {registration.checkout_session_id && !registration.strippenkaart_id && (
                                      <Badge variant="outline" className="text-xs mt-1 ml-1 bg-blue-50">
                                        Los ticket
                                      </Badge>
                                    )}
                                    <p className="text-xs text-gray-500 mt-2">
                                      Ingeschreven: {new Date(registration.aangemeld_op).toLocaleString('nl-NL')}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            ))}
                          {eventRegistrations.filter(r => r.rol === 'representant').length === 0 && (
                            <p className="text-sm text-gray-500 text-center py-4">Geen representanten ingeschreven</p>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {!selectedEventId && (
              <Card>
                <CardContent className="py-12 text-center">
                  <Calendar className="h-12 w-12 mx-auto mb-4" style={{ color: '#6b4912' }} />
                  <p className="text-sm" style={{ color: '#6b4912' }}>Selecteer eerst een evenement</p>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Uitnodigingen Sectie */}
        {activeTab === 'invitations' && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <UserPlus className="h-5 w-5" style={{ color: '#854C73' }} />
                  Uitnodigen voor <span className="italic font-light font-serif">Opstellingenmiddag</span>
                </CardTitle>
                <CardDescription>
                  Selecteer gebruikers om uit te nodigen voor een nieuwe opstellingenmiddag. Ze kunnen zich vervolgens inschrijven als representant.
                </CardDescription>
              </CardHeader>
            </Card>

            {loading ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
                  <p className="mt-4 text-sm" style={{ color: '#6b4912' }}>Laden...</p>
                </CardContent>
              </Card>
            ) : users.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Users className="h-12 w-12 mx-auto mb-4" style={{ color: '#6b4912' }} />
                  <p className="text-sm" style={{ color: '#6b4912' }}>Nog geen geregistreerde gebruikers</p>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Selectie Bar */}
                <Card className="mb-4">
                  <CardContent className="pt-6">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          id="select-all-users"
                          checked={selectedUsers.size === users.length && users.length > 0}
                          onChange={(e) => handleSelectAll(e.target.checked)}
                          className="w-4 h-4 rounded border-gray-300 text-[#854C73] focus:ring-[#854C73]"
                        />
                        <Label htmlFor="select-all-users" className="text-sm font-medium cursor-pointer">
                          Selecteer alle ({users.length})
                        </Label>
                      </div>
                      {selectedUsers.size > 0 && (
                        <div className="flex items-center gap-3">
                          <span className="text-sm text-gray-600">
                            {selectedUsers.size} {selectedUsers.size === 1 ? 'gebruiker' : 'gebruikers'} geselecteerd
                          </span>
                          <Button 
                            onClick={() => setEmailModalOpen(true)}
                            className="bg-[#854C73] hover:bg-[#590D0D]"
                          >
                            <Send className="h-4 w-4 mr-2" />
                            Verstuur uitnodiging
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Gebruikers Lijst */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="h-5 w-5" style={{ color: '#854C73' }} />
                      Alle Gebruikers
                      <Badge variant="secondary" className="ml-auto">
                        {users.length}
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {users.map((userData) => {
                        const hasStrippenkaart = userData.strippenkaarten.length > 0;
                        const totalStrippen = userData.strippenkaarten.reduce((sum, k) => sum + k.resterende_strippen, 0);
                        
                        return (
                          <div
                            key={userData.id}
                            className={`p-4 border rounded-lg bg-[#FAEBEE]/30 border-[#854C73]/20 ${selectedUsers.has(userData.id) ? 'ring-2 ring-[#854C73]' : ''}`}
                          >
                            <div className="flex items-start gap-3">
                              <input
                                type="checkbox"
                                checked={selectedUsers.has(userData.id)}
                                onChange={(e) => handleSelectUser(userData.id, e.target.checked)}
                                className="mt-1 w-4 h-4 rounded border-gray-300 text-[#854C73] focus:ring-[#854C73] flex-shrink-0"
                              />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <Mail className="h-4 w-4 flex-shrink-0" style={{ color: '#854C73' }} />
                                  <span className="font-medium text-sm break-all">
                                    {userData.email}
                                  </span>
                                  {userData.email && userData.email !== 'Email onbekend' && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-6 w-6 p-0 hover:bg-[#854C73]/10"
                                      onClick={() => copyEmailToClipboard(userData.email)}
                                      title="Kopieer emailadres"
                                    >
                                      {copiedEmail === userData.email ? (
                                        <Check className="h-3.5 w-3.5" style={{ color: '#854C73' }} />
                                      ) : (
                                        <Copy className="h-3.5 w-3.5" style={{ color: '#854C73' }} />
                                      )}
                                    </Button>
                                  )}
                                </div>
                                <div className="flex flex-wrap gap-2 mt-2">
                                  <Badge variant="outline" className="text-xs">
                                    Geregistreerd: {new Date(userData.created_at).toLocaleDateString('nl-NL')}
                                  </Badge>
                                  {hasStrippenkaart ? (
                                    <Badge variant="outline" className="text-xs bg-green-50">
                                      {totalStrippen} {totalStrippen === 1 ? 'strip' : 'strippen'} beschikbaar
                                    </Badge>
                                  ) : (
                                    <Badge variant="outline" className="text-xs bg-yellow-50">
                                      Geen strippenkaart
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        )}

        {/* Users List - Cards View */}
        {activeTab === 'users' && viewMode === 'cards' && (
          <div className="space-y-4 md:space-y-6">
            {users.length === 0 ? (
            <Card>
              <CardContent className="py-8 md:py-12 text-center">
                <Users className="h-10 w-10 md:h-12 md:w-12 mx-auto mb-3 md:mb-4" style={{ color: '#6b4912' }} />
                <p className="text-sm md:text-base" style={{ color: '#6b4912' }}>Nog geen gebruikers met strippenkaarten</p>
              </CardContent>
            </Card>
          ) : (
            users.map((userData) => {
              const totalStrippen = userData.strippenkaarten.reduce((sum, k) => sum + k.resterende_strippen, 0);
              const totalPurchased = userData.strippenkaarten.reduce((sum, k) => sum + k.totaal_strippen, 0);
              const totalUsed = totalPurchased - totalStrippen;
              const usagePercentage = totalPurchased > 0 ? (totalUsed / totalPurchased) * 100 : 0;

              return (
                <Card key={userData.id} className="hover:shadow-lg transition-shadow">
                  <CardHeader className="pb-3">
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <CardTitle className="flex items-center gap-2 text-base md:text-lg break-all">
                          <Mail className="h-4 w-4 md:h-5 md:w-5 flex-shrink-0" style={{ color: '#6b4912' }} />
                          <span className="break-all">{userData.email}</span>
                        </CardTitle>
                        <CardDescription className="text-xs md:text-sm">
                          Lid sinds {new Date(userData.created_at).toLocaleDateString('nl-NL')}
                        </CardDescription>
                      </div>
                      <Button
                        onClick={() => addStrippenkaart(userData.id)}
                        disabled={actionLoading === userData.id}
                        variant="outline"
                        size="sm"
                        className="w-full sm:w-auto shrink-0"
                      >
                        <Ticket className="mr-2 h-4 w-4" />
                        <span className="text-xs md:text-sm">Voeg Kaart Toe</span>
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {/* Strippenkaarten */}
                    <div className="space-y-3">
                      {/* Actieve Strippenkaarten */}
                      {userData.strippenkaarten.filter(k => !k.archived).length > 0 && (
                        <>
                          <h4 className="text-xs md:text-sm font-medium mb-3" style={{ color: '#6b4912' }}>
                            Actieve Strippenkaarten ({userData.strippenkaarten.filter(k => !k.archived).length})
                          </h4>
                          {userData.strippenkaarten.filter(k => !k.archived).map((kaart) => {
                            const gebruikteStrippen = kaart.totaal_strippen - kaart.resterende_strippen;
                            const kaartPercentage = (gebruikteStrippen / kaart.totaal_strippen) * 100;
                            return (
                              <div key={kaart.id} className="p-3 md:p-4 border rounded-lg bg-white">
                                <div className="flex justify-between items-start mb-3">
                                  <div>
                                    <span className="text-xs md:text-sm font-medium">
                                      {kaart.resterende_strippen} / {kaart.totaal_strippen} strippen
                                    </span>
                                    <p className="text-xs text-gray-500 mt-0.5">
                                      Gekocht: {new Date(kaart.gekocht_op).toLocaleDateString('nl-NL')}
                                    </p>
                                  </div>
                                </div>
                                <StrippenkaartVisual 
                                  resterende={kaart.resterende_strippen} 
                                  totaal={kaart.totaal_strippen}
                                  className="mb-3"
                                />
                                <div className="grid grid-cols-2 sm:flex gap-2">
                                  <Button
                                    onClick={() => adjustStrippen(kaart.id, -1)}
                                    disabled={actionLoading === kaart.id || kaart.resterende_strippen === 0}
                                    variant="outline"
                                    size="sm"
                                    className="text-xs sm:text-sm"
                                  >
                                    <Minus className="h-3 w-3 sm:mr-1" />
                                    <span className="hidden sm:inline">Strip eraf</span>
                                  </Button>
                                  <Button
                                    onClick={() => adjustStrippen(kaart.id, 1)}
                                    disabled={actionLoading === kaart.id || kaart.resterende_strippen >= kaart.totaal_strippen}
                                    variant="outline"
                                    size="sm"
                                    className="text-xs sm:text-sm"
                                  >
                                    <Plus className="h-3 w-3 sm:mr-1" />
                                    <span className="hidden sm:inline">Strip erbij</span>
                                  </Button>
                                  <Button
                                    onClick={() => toggleArchive(kaart.id, kaart.archived || false)}
                                    disabled={actionLoading === kaart.id}
                                    variant="outline"
                                    size="sm"
                                    className="text-xs sm:text-sm"
                                  >
                                    <Archive className="h-3 w-3 sm:mr-1" />
                                    <span className="hidden sm:inline">Archiveer</span>
                                  </Button>
                                  <Button
                                    onClick={() => deleteStrippenkaart(kaart.id)}
                                    disabled={actionLoading === kaart.id}
                                    variant="outline"
                                    size="sm"
                                    className="text-xs sm:text-sm"
                                    style={{ color: '#7a0003' }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.color = '#7a0003';
                                      e.currentTarget.style.backgroundColor = '#faebee';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.color = '#7a0003';
                                      e.currentTarget.style.backgroundColor = 'transparent';
                                    }}
                                  >
                                    <Trash2 className="h-3 w-3 sm:mr-1" />
                                    <span className="hidden sm:inline">Verwijder</span>
                                  </Button>
                                </div>
                              </div>
                            );
                          })}
                        </>
                      )}

                      {/* Gearchiveerde Strippenkaarten - Inklapbaar */}
                      {userData.strippenkaarten.filter(k => k.archived).length > 0 && (
                        <div className="mt-4 md:mt-6">
                          <button
                            onClick={() => {
                              const newExpanded = new Set(expandedArchives);
                              if (newExpanded.has(userData.id)) {
                                newExpanded.delete(userData.id);
                              } else {
                                newExpanded.add(userData.id);
                              }
                              setExpandedArchives(newExpanded);
                            }}
                            className="flex items-center gap-2 text-xs md:text-sm font-medium transition-colors w-full py-2 px-3 rounded-lg"
                            style={{ color: '#6b4912' }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.color = '#854c73';
                              e.currentTarget.style.backgroundColor = '#faebee';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.color = '#6b4912';
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                          >
                            {expandedArchives.has(userData.id) ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                            Gearchiveerd ({userData.strippenkaarten.filter(k => k.archived).length})
                          </button>
                          
                          {expandedArchives.has(userData.id) && (
                            <div className="mt-3 space-y-3 animate-in fade-in-50 slide-in-from-top-2 duration-200">
                              {userData.strippenkaarten.filter(k => k.archived).map((kaart) => {
                                const gebruikteStrippen = kaart.totaal_strippen - kaart.resterende_strippen;
                                const kaartPercentage = (gebruikteStrippen / kaart.totaal_strippen) * 100;
                                return (
                                  <div key={kaart.id} className="p-3 md:p-4 border rounded-lg opacity-60" style={{ backgroundColor: '#efe8dc', borderColor: '#e3d9c4' }}>
                                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 mb-3">
                                      <div>
                                        <span className="text-xs md:text-sm font-medium" style={{ color: '#6b4912' }}>
                                          {kaart.resterende_strippen} / {kaart.totaal_strippen} strippen
                                        </span>
                                        <p className="text-xs mt-0.5" style={{ color: '#6b4912' }}>
                                          Gekocht: {new Date(kaart.gekocht_op).toLocaleDateString('nl-NL')}
                                        </p>
                                      </div>
                                      <Badge variant="secondary" className="text-xs w-fit">Gearchiveerd</Badge>
                                    </div>
                                    <StrippenkaartVisual 
                                      resterende={kaart.resterende_strippen} 
                                      totaal={kaart.totaal_strippen}
                                      className="mb-3"
                                    />
                                    <div className="flex flex-col sm:flex-row gap-2">
                                      <Button
                                        onClick={() => toggleArchive(kaart.id, kaart.archived)}
                                        disabled={actionLoading === kaart.id}
                                        variant="outline"
                                        size="sm"
                                        className="text-xs sm:text-sm w-full sm:w-auto"
                                      >
                                        <ArchiveRestore className="h-3 w-3 sm:mr-1" />
                                        <span className="hidden sm:inline">Terugzetten</span>
                                        <span className="sm:hidden">Terug</span>
                                      </Button>
                                      <Button
                                        onClick={() => deleteStrippenkaart(kaart.id)}
                                        disabled={actionLoading === kaart.id}
                                        variant="outline"
                                        size="sm"
                                        className="text-red-600 hover:text-red-700 hover:bg-red-50 text-xs sm:text-sm w-full sm:w-auto"
                                      >
                                        <Trash2 className="h-3 w-3 sm:mr-1" />
                                        Verwijder
                                      </Button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
          </div>
        )}

        {/* Users List - Table View */}
        {activeTab === 'users' && viewMode === 'table' && (
          <>
            {/* Mobile warning */}
            <div className="md:hidden bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <p className="text-sm" style={{ color: '#854c73' }}>
                💡 Tip: De tabelweergave werkt beter op grotere schermen. Probeer de card-weergave voor mobiel.
              </p>
            </div>
            
            <div className="bg-white rounded-lg shadow overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 md:px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: '#6b4912' }}>
                      Gebruiker
                    </th>
                    <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                      Product
                    </th>
                    <th className="px-3 md:px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: '#6b4912' }}>
                      Status
                    </th>
                    <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                      Gekocht op
                    </th>
                    <th className="px-3 md:px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: '#6b4912' }}>
                      Acties
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.flatMap((user) =>
                    user.strippenkaarten
                      .filter(k => !k.archived)
                      .map((kaart) => (
                        <tr key={kaart.id} style={{ backgroundColor: 'transparent' }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#faebee'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                          <td className="px-3 md:px-6 py-4">
                            <div className="text-xs md:text-sm font-medium break-all" style={{ color: '#6b4912' }}>{user.email}</div>
                            <div className="text-xs hidden sm:block" style={{ color: '#6b4912' }}>
                              Lid sinds {new Date(user.created_at).toLocaleDateString('nl-NL')}
                            </div>
                          </td>
                          <td className="px-3 md:px-6 py-4 whitespace-nowrap hidden sm:table-cell">
                            <div className="text-xs md:text-sm" style={{ color: '#6b4912' }}>
                              {kaart.totaal_strippen}x
                            </div>
                          </td>
                          <td className="px-3 md:px-6 py-4">
                            <div className="flex items-center gap-2">
                              <div className="flex-1 min-w-[120px] md:min-w-[200px]">
                                <StrippenkaartVisual 
                                  resterende={kaart.resterende_strippen} 
                                  totaal={kaart.totaal_strippen}
                                />
                              </div>
                              <span className="text-xs md:text-sm font-medium whitespace-nowrap">
                                {kaart.resterende_strippen}/{kaart.totaal_strippen}
                              </span>
                            </div>
                          </td>
                          <td className="px-3 md:px-6 py-4 whitespace-nowrap text-xs md:text-sm hidden lg:table-cell" style={{ color: '#6b4912' }}>
                            {new Date(kaart.gekocht_op).toLocaleDateString('nl-NL')}
                          </td>
                          <td className="px-3 md:px-6 py-4 whitespace-nowrap text-sm">
                            <div className="flex gap-0.5 md:gap-1">
                              <Button
                                onClick={() => adjustStrippen(kaart.id, -1)}
                                disabled={actionLoading === kaart.id || kaart.resterende_strippen === 0}
                                variant="ghost"
                                size="sm"
                                title="Strip eraf"
                                className="p-1 md:p-2"
                              >
                                <Minus className="h-3 w-3 md:h-4 md:w-4" />
                              </Button>
                              <Button
                                onClick={() => adjustStrippen(kaart.id, 1)}
                                disabled={actionLoading === kaart.id || kaart.resterende_strippen >= kaart.totaal_strippen}
                                variant="ghost"
                                size="sm"
                                title="Strip erbij"
                                className="p-1 md:p-2"
                              >
                                <Plus className="h-3 w-3 md:h-4 md:w-4" />
                              </Button>
                              <Button
                                onClick={() => toggleArchive(kaart.id, kaart.archived || false)}
                                disabled={actionLoading === kaart.id}
                                variant="ghost"
                                size="sm"
                                title="Archiveer"
                                className="p-1 md:p-2 hidden sm:inline-flex"
                              >
                                <Archive className="h-3 w-3 md:h-4 md:w-4" />
                              </Button>
                              <Button
                                onClick={() => deleteStrippenkaart(kaart.id)}
                                disabled={actionLoading === kaart.id}
                                variant="ghost"
                                size="sm"
                                className="p-1 md:p-2 hidden sm:inline-flex"
                                style={{ color: '#7a0003' }}
                                onMouseEnter={(e) => e.currentTarget.style.color = '#7a0003'}
                                onMouseLeave={(e) => e.currentTarget.style.color = '#7a0003'}
                                title="Verwijder"
                              >
                                <Trash2 className="h-3 w-3 md:h-4 md:w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                  )}
                </tbody>
              </table>
              {users.length === 0 && (
                <div className="text-center py-8 md:py-12 px-4">
                  <Users className="h-10 w-10 md:h-12 md:w-12 mx-auto mb-3 md:mb-4" style={{ color: '#6b4912' }} />
                  <p className="text-sm md:text-base" style={{ color: '#6b4912' }}>Nog geen gebruikers met strippenkaarten</p>
                </div>
              )}
            </div>
          </>
        )}

        {/* Email Verzend Modal */}
        <Dialog open={emailModalOpen} onOpenChange={setEmailModalOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Email versturen</DialogTitle>
              <DialogDescription>
                Verstuur een email naar {getSelectedEmails().length} {getSelectedEmails().length === 1 ? 'geselecteerde persoon' : 'geselecteerde personen'}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="email-subject">Onderwerp *</Label>
                <Input
                  id="email-subject"
                  placeholder="Bijv: Belangrijke informatie over de opstellingenmiddag"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  disabled={sendingEmail}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email-message">Bericht *</Label>
                <Textarea
                  id="email-message"
                  placeholder="Typ hier je bericht..."
                  value={emailMessage}
                  onChange={(e) => setEmailMessage(e.target.value)}
                  disabled={sendingEmail}
                  rows={10}
                  className="resize-none"
                />
                <p className="text-xs text-gray-500">
                  Je bericht wordt automatisch opgemaakt met de Systemique huisstijl.
                </p>
              </div>

              {getSelectedEmails().length > 0 && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs font-medium mb-2">Ontvangers ({getSelectedEmails().length}):</p>
                  <div className="max-h-32 overflow-y-auto">
                    <div className="flex flex-wrap gap-1">
                      {getSelectedEmails().slice(0, 10).map((email, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs">
                          {email}
                        </Badge>
                      ))}
                      {getSelectedEmails().length > 10 && (
                        <Badge variant="outline" className="text-xs">
                          +{getSelectedEmails().length - 10} meer
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setEmailModalOpen(false);
                  setEmailSubject('');
                  setEmailMessage('');
                }}
                disabled={sendingEmail}
              >
                Annuleren
              </Button>
              <Button
                onClick={sendEmails}
                disabled={sendingEmail || !emailSubject.trim() || !emailMessage.trim()}
                className="bg-[#854C73] hover:bg-[#590D0D]"
              >
                {sendingEmail ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Verzenden...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Verstuur email
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

